import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import { resetTurboFrame } from 'helpers/turbo_frames'

// DEPRECATED. Drives the legacy Bootstrap modal in the global `turbo-frame#modal`.
//
// It closes itself on turbo:before-fetch-request, so which fetches count as navigating away is
// load-bearing for ~200 views — see docs/turbo_2.0.23_update.md before touching that guard.
//
// Do not extend this. New modals use UI::ModalComponent; migrate these when you touch them:
// .claude/skills/migrate-to-galette-modal/SKILL.md
//
// See https://turbo.hotwired.dev/reference/events
//
export default class extends Controller {
  static values = {
    confirmation: Boolean,
    closeBeforeFetch: Boolean
  }
  // Runs any time an element with this controller enters the DOM.
  //
  // This is a great place to setup event listeners so you can build complex
  // behaviors using Bootstrap components like Modal and Popper.
  //

  connect() {
    this.originalFocusElement = document.activeElement
    // There is only One #modal element in the DOM. It should be in the layout, you probably don't need to add one.
    // This is the root element, a sub element attached this controller and this activates the modal behavior.
    Modal.getOrCreateInstance('#modal').show()

    // If the modal is a confirmation dialog, listen for an AJAX event to close the modal.
    // If there is any validation error, just render new content for `#modal` and it will work.
    if (this.confirmationValue) {
      document.body.addEventListener('ajax:send', this.closeModal)
      document.body.addEventListener('turbo:submit-end', this.closeTurboModal)
    }

    // If we visit a page, also dispose of the modal.
    //
    // See https://turbo.hotwired.dev/handbook/drive#pausing-requests
    //
    this.closeModalBeforeFetch = this.closeModalBeforeFetch.bind(this)
    document.addEventListener('turbo:before-fetch-request', this.closeModalBeforeFetch)

    // Make sure we dispose of a modal after it's hidden
    document.getElementById('modal').addEventListener('hidden.bs.modal', this.disposeModal)
  }

  // Runs any time an element with this controller is removed from the DOM.
  //
  disconnect() {
    if (this.originalFocusElement) {
      this.originalFocusElement.focus()
    }
    // Deregister listeners.
    // See https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/removeEventListener#matching_event_listeners_for_removal
    //
    if (this.confirmationValue) {
      document.body.removeEventListener('ajax:send', this.closeModal)
      document.body.removeEventListener('turbo:submit-end', this.closeTurboModal)
    }

    const modal = document.getElementById('modal')
    if (modal) {
      modal.removeEventListener('hidden.bs.modal', this.disposeModal)
    }

    document.removeEventListener('turbo:before-fetch-request', this.closeModalBeforeFetch)
  }

  // Event Handlers
  //
  closeModal() {
    Modal.getOrCreateInstance('#modal').hide()
  }

  closeTurboModal(e) {
    if (e.detail.success) {
      Modal.getOrCreateInstance('#modal').hide()
    }
  }

  disposeModal() {
    const modal = Modal.getOrCreateInstance('#modal')
    if (modal._backdrop) {
      modal.dispose()
    }

    // Unloaded, not just emptied: reopening re-sets the url the frame already holds, which fires no
    // attributeChangedCallback, so Turbo would never drop [complete] and refetch.
    resetTurboFrame(document.getElementById('modal'))
  }

  closeModalBeforeFetch(event) {
    let modalEl = document.getElementById('modal')

    if (modalEl === null || modalEl.style.display !== 'block') return

    if (this.closeBeforeFetchValue || this.userNavigation(event)) {
      // A fetch the modal's own frame is making is a chained modal, not a navigation away: leave
      // [src] alone or we cancel the request we are about to render (hotwired/turbo#1399).
      this.tearDownModal(modalEl, { unload: event.target !== modalEl })
    }
  }

  // Closes the dialog synchronously and lets the request proceed, rather than pausing the fetch
  // until Bootstrap's hide animation ends — a resume Bootstrap does not always deliver. See
  // docs/turbo_2.0.23_update.md.
  tearDownModal(modalEl, { unload } = { unload: true }) {
    Modal.getOrCreateInstance(modalEl).dispose()

    modalEl.style.removeProperty('display')
    modalEl.classList.remove('show')
    modalEl.removeAttribute('aria-modal')
    modalEl.setAttribute('aria-hidden', 'true')

    if (unload) {
      resetTurboFrame(modalEl)
    } else {
      modalEl.innerHTML = ''
    }

    document.querySelector('.modal-backdrop')?.remove()
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('padding-right')
    document.body.style.removeProperty('overflow')
  }

  // Background fetches must not close an open confirm dialog out from under the user: a lazy
  // turbo-frame elsewhere on the page loading late, or a link hover prefetch, is not a navigation.
  // Everything else keeps the pre-existing behavior (any GET text/html fetch closes the modal,
  // including links inside the modal that navigate another frame).
  userNavigation(event) {
    const frame = event.target instanceof Element && event.target.closest('turbo-frame')
    if (frame && frame.getAttribute('loading') === 'lazy') return false
    if (event.detail.fetchOptions.headers['X-Sec-Purpose'] === 'prefetch') return false

    return event.detail.fetchOptions.method === 'GET' && event.detail.fetchOptions.headers.Accept.match('text/html')
  }
}
